import { TrendingUpIcon, TrendingDownIcon, MinusIcon } from "lucide-react";
import { cn } from "@util/tailwind";

interface AttemptPoint {
    attempt_number: number;
    total_score: number | null;
}

interface Props {
    attempts: AttemptPoint[];
    width?: number;
    height?: number;
    showThreshold?: boolean;
    // Richer treatment for the larger drawer chart: per-point score labels and
    // a labelled "strong" threshold. Left off for the compact card sparkline
    // so it stays uncluttered.
    showValues?: boolean;
}

const MAX_SCORE = 100;
const STRONG_THRESHOLD = 72;

export function ScoreSparkline({
    attempts,
    width = 220,
    height = 60,
    showThreshold = true,
    showValues = false,
}: Props) {
    const scored = attempts.filter((a) => typeof a.total_score === "number");
    if (scored.length === 0) {
        return (
            <div
                style={{ width, height }}
                className="text-xs text-gray-400 italic flex items-center justify-center"
            >
                No attempts scored yet.
            </div>
        );
    }

    const padX = 10;
    const padTop = showValues ? 18 : 8;
    const padBottom = 8;
    const innerW = width - padX * 2;
    const innerH = height - padTop - padBottom;
    const baseY = padTop + innerH;
    const points = scored.map((a, i) => {
        const x = scored.length === 1 ? padX + innerW / 2 : padX + (i / (scored.length - 1)) * innerW;
        const y = padTop + innerH - (a.total_score! / MAX_SCORE) * innerH;
        return { x, y, score: a.total_score! };
    });
    const polyline = points.map((p) => `${p.x},${p.y}`).join(" ");
    const areaPath = `M ${points[0].x},${baseY} L ${points
        .map((p) => `${p.x},${p.y}`)
        .join(" L ")} L ${points[points.length - 1].x},${baseY} Z`;
    const thresholdY = padTop + innerH - (STRONG_THRESHOLD / MAX_SCORE) * innerH;

    const last = points[points.length - 1].score;
    const first = points[0].score;
    const delta = scored.length > 1 ? last - first : 0;

    const TrendIcon = delta > 0 ? TrendingUpIcon : delta < 0 ? TrendingDownIcon : MinusIcon;
    const chipCls =
        delta > 0
            ? "bg-emerald-50 text-emerald-700"
            : delta < 0
            ? "bg-rose-50 text-rose-700"
            : "bg-gray-100 text-gray-600";

    return (
        <div className="flex items-center gap-4">
            <svg width={width} height={height} className="overflow-visible flex-shrink-0">
                <defs>
                    <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.18" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                    </linearGradient>
                </defs>
                {showThreshold && (
                    <>
                        <line
                            x1={padX}
                            y1={thresholdY}
                            x2={width - padX}
                            y2={thresholdY}
                            stroke="#e2e8f0"
                            strokeWidth="1"
                            strokeDasharray="3 3"
                        />
                        {showValues && (
                            <text
                                x={width - padX}
                                y={thresholdY - 4}
                                textAnchor="end"
                                fontSize="9"
                                className="fill-gray-300"
                            >
                                Strong {STRONG_THRESHOLD}
                            </text>
                        )}
                    </>
                )}
                {scored.length > 1 && <path d={areaPath} fill="url(#sparkFill)" />}
                {scored.length > 1 && (
                    <polyline
                        points={polyline}
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                )}
                {points.map((p, i) => {
                    const isLast = i === points.length - 1;
                    return (
                        <g key={i}>
                            {showValues && (
                                <text
                                    x={p.x}
                                    y={p.y - 8}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fontWeight={isLast ? 700 : 600}
                                    className={isLast ? "fill-blue-600" : "fill-gray-400"}
                                >
                                    {p.score}
                                </text>
                            )}
                            <circle
                                cx={p.x}
                                cy={p.y}
                                r={isLast ? 4 : 3}
                                fill={isLast ? "#3b82f6" : "#fff"}
                                stroke="#3b82f6"
                                strokeWidth="2"
                            />
                        </g>
                    );
                })}
            </svg>
            {scored.length > 1 && (
                <div className="flex-shrink-0 whitespace-nowrap">
                    <span
                        className={cn(
                            "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums",
                            chipCls
                        )}
                    >
                        <TrendIcon className="h-3 w-3" />
                        {delta > 0 ? `+${delta}` : delta}
                    </span>
                    <div className="mt-1 text-[11px] text-gray-400">since attempt 1</div>
                </div>
            )}
        </div>
    );
}
